import { mergeAttributes, Node } from '@tiptap/core';
import { wrappingInputRule } from '@tiptap/core';

export interface TaskItemOptions {
  nested: boolean;
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    taskItem: {
      /**
       * Toggle a task item
       */
      toggleTaskItem: () => ReturnType;
    };
  }
}

export const inputRegex = /^\s*(\[([( |x])?\])\s$/;

export const TaskItemCustom = Node.create<TaskItemOptions>({
  name: 'taskItem',

  addOptions() {
    return {
      nested: false,
      HTMLAttributes: {},
    };
  },

  content() {
    return this.options.nested ? 'paragraph block*' : 'paragraph+';
  },

  defining: true,

  addAttributes() {
    return {
      checked: {
        default: false,
        keepOnSplit: false,
        parseHTML: element => element.getAttribute('data-checked') === 'true',
        renderHTML: attributes => ({
          'data-checked': attributes['checked'],
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: `li[data-type="${this.name}"]`,
        priority: 51,
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'li',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-type': this.name,
      }),
      [
        'label',
        {
          contenteditable: 'false',
          style: 'display: inline-block; margin: 0; padding: 0; flex: 0 0 auto;',
        },
        [
          'input',
          {
            type: 'checkbox',
            checked: node.attrs['checked'] ? 'checked' : null,
            style: 'margin: 0; padding: 0; cursor: pointer;',
          },
        ],
      ],
      [
        'div',
        {
          style: 'display: inline-block; flex: 1 1 auto; min-width: 0;',
        },
        0,
      ],
    ];
  },

  addKeyboardShortcuts() {
    const shortcuts = {
      Enter: () => this.editor.commands.splitListItem(this.name),
      'Shift-Tab': () => this.editor.commands.liftListItem(this.name),
    };

    if (!this.options.nested) {
      return shortcuts;
    }

    return {
      ...shortcuts,
      Tab: () => this.editor.commands.sinkListItem(this.name),
    };
  },

  addNodeView() {
    return ({ node, HTMLAttributes, getPos, editor }) => {
      const listItem = document.createElement('li');
      listItem.dataset['type'] = this.name;
      
      if (node.attrs['checked']) {
        listItem.dataset['checked'] = 'true';
      }

      // Apply any custom HTML attributes
      Object.keys(this.options.HTMLAttributes).forEach(key => {
        listItem.setAttribute(key, this.options.HTMLAttributes[key]);
      });
      Object.keys(HTMLAttributes).forEach(key => {
        listItem.setAttribute(key, HTMLAttributes[key]);
      });

      // Style the list item as flexbox
      listItem.style.display = 'flex';
      listItem.style.flexDirection = 'row';
      listItem.style.alignItems = 'flex-start';
      listItem.style.gap = '0.5rem';
      listItem.style.listStyle = 'none';
      listItem.style.paddingLeft = '0';
      listItem.style.margin = '0.25rem 0';

      // Create checkbox container
      const label = document.createElement('label');
      label.contentEditable = 'false';
      label.style.flex = '0 0 auto';
      label.style.margin = '0';
      label.style.padding = '0';
      label.style.userSelect = 'none';
      label.style.display = 'flex';
      label.style.alignItems = 'center';

      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.checked = node.attrs['checked'];
      checkbox.style.margin = '0';
      checkbox.style.padding = '0';
      checkbox.style.cursor = 'pointer';
      checkbox.style.width = '1rem';
      checkbox.style.height = '1rem';

      checkbox.addEventListener('change', (event) => {
        if (typeof getPos === 'function') {
          const pos = getPos();
          if (pos !== undefined) {
            const transaction = editor.view.state.tr.setNodeMarkup(pos, undefined, {
              ...node.attrs,
              checked: (event.target as HTMLInputElement).checked,
            });
            editor.view.dispatch(transaction);
          }
        }
      });

      label.appendChild(checkbox);
      listItem.appendChild(label);

      // Create content container
      const content = document.createElement('div');
      content.style.flex = '1 1 auto';
      content.style.minWidth = '0';
      content.style.margin = '0';

      // Apply checked styling
      if (node.attrs['checked']) {
        content.style.color = '#9ca3af';
        content.style.fontStyle = 'italic';
        content.style.textDecoration = 'line-through';
      }

      listItem.appendChild(content);

      return {
        dom: listItem,
        contentDOM: content,
        update: (updatedNode) => {
          if (updatedNode.type.name !== this.name) {
            return false;
          }

          // Update checked state
          checkbox.checked = updatedNode.attrs['checked'];
          
          if (updatedNode.attrs['checked']) {
            listItem.dataset['checked'] = 'true';
            content.style.color = '#9ca3af';
            content.style.fontStyle = 'italic';
            content.style.textDecoration = 'line-through';
          } else {
            delete listItem.dataset['checked'];
            content.style.color = '';
            content.style.fontStyle = '';
            content.style.textDecoration = '';
          }

          return true;
        },
      };
    };
  },

  addInputRules() {
    return [
      wrappingInputRule({
        find: inputRegex,
        type: this.type,
        getAttributes: match => ({
          checked: match[match.length - 1] === 'x',
        }),
      }),
    ];
  },
});
